/**
 * VanityRouter — chooses the history implementation based on the host:
 *
 *   - Default host (localhost, *.run.app, the platform domain, or any
 *     host that maps to no tenant) → plain <BrowserRouter>. ZERO change
 *     to the normal product; slugs stay in the URL as before.
 *
 *   - Vanity host (a custom domain mapped to a tenant, e.g.
 *     jewishpro.ai → m2-team) → <HistoryRouter> driven by the
 *     slug-hiding history, so the tenant slug is rendered internally but
 *     never shown in the address bar.
 *
 * The host→slug answer is read synchronously from cache when possible
 * (no flash on repeat visits). On a custom domain's very first visit
 * there's no cache yet, so we briefly show a loader while the single
 * `/public/brand-from-host` call resolves, then commit to a router. We
 * never optimistically mount BrowserRouter for an unknown custom domain
 * — doing so would flash the slug into the URL before switching.
 */

import { useEffect, useRef, useState } from 'react';
import { BrowserRouter, unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { LoadingScreen } from '@/components/common/LoadingScreen';
import { getCachedHostSlug, isKnownDefaultHost, resolveHostSlug } from './vanityHost';
import { createSlugHidingHistory, type SlugHidingHistory } from './vanityHistory';

type SlugState = string | null | undefined; // undefined = still resolving

export function VanityRouter({ children }: { children: React.ReactNode }) {
  const host = typeof window !== 'undefined' ? window.location.host : '';

  // Best synchronous guess: cached answer, or `null` for hosts we know
  // can't be vanity. Only a genuinely-unknown custom domain stays
  // `undefined` and triggers the async resolve below.
  const [slug, setSlug] = useState<SlugState>(() => {
    const cached = getCachedHostSlug(host);
    if (cached !== undefined) return cached;
    return isKnownDefaultHost(host) ? null : undefined;
  });

  useEffect(() => {
    if (slug !== undefined) return;
    let cancelled = false;
    resolveHostSlug(host).then((s) => { if (!cancelled) setSlug(s); });
    return () => { cancelled = true; };
  }, [host, slug]);

  // One slug-hiding history per vanity slug, created lazily and kept
  // stable for the life of the component.
  const historyRef = useRef<SlugHidingHistory | null>(null);
  if (slug && !historyRef.current) {
    historyRef.current = createSlugHidingHistory(slug);
  }

  if (slug === undefined) return <LoadingScreen />;
  if (!slug) return <BrowserRouter>{children}</BrowserRouter>;
  // `history` typing comes from the (absent) `history` package; the
  // structural shape we provide is what HistoryRouter actually uses.
  return (
    <HistoryRouter history={historyRef.current as unknown as never}>
      {children}
    </HistoryRouter>
  );
}
